import { type Static, Type } from "@sinclair/typebox"
import { CardColorSchema } from "./colors"
import { IdSchema, NonEmptyStringSchema } from "./common"
import { CardArtworkSchema } from "./media"
import { SkillSelectionSchema } from "./skills"

export const CardKindSchema = Type.Union([
  Type.Literal("starter"),
  Type.Literal("authored_unit"),
  Type.Literal("better_battle_bot"),
  Type.Literal("hero"),
])

export type CardKind = Static<typeof CardKindSchema>

export const CardStatsSchema = Type.Object({
  attack: Type.Integer({
    minimum: 0,
  }),
  health: Type.Integer({
    minimum: 1,
  }),
})

export type CardStats = Static<typeof CardStatsSchema>

export const TextOverridesSchema = Type.Object({
  name: Type.Optional(NonEmptyStringSchema),
  rulesText: Type.Optional(NonEmptyStringSchema),
})

export type TextOverrides = Static<typeof TextOverridesSchema>

export const AuthoredCostInputsSchema = Type.Object({
  skillCostDelta: Type.Number({
    minimum: -4,
    multipleOf: 0.5,
  }),
  nonExhaustSurcharge: Type.Number({
    minimum: 0,
    multipleOf: 0.5,
  }),
  selfDestructSurcharge: Type.Number({
    minimum: -2,
    multipleOf: 0.5,
  }),
  secondaryColorSurcharge: Type.Union([Type.Literal(0), Type.Literal(0.5)]),
})

export type AuthoredCostInputs = Static<typeof AuthoredCostInputsSchema>

export const HeroLevelCardSchema = Type.Object({
  stats: CardStatsSchema,
  selectedSkills: Type.Array(SkillSelectionSchema),
  textOverrides: Type.Optional(TextOverridesSchema),
})

export type HeroLevelCard = Static<typeof HeroLevelCardSchema>

export const HeroProgressionSchema = Type.Object({
  level2: HeroLevelCardSchema,
  level3: HeroLevelCardSchema,
})

export type HeroProgression = Static<typeof HeroProgressionSchema>

export const AuthoredCardSchema = Type.Object({
  id: IdSchema,
  kind: CardKindSchema,
  name: NonEmptyStringSchema,
  primaryColor: CardColorSchema,
  secondaryColor: Type.Optional(CardColorSchema),
  artwork: Type.Optional(CardArtworkSchema),
  stats: CardStatsSchema,
  selectedSkills: Type.Array(SkillSelectionSchema),
  authoredCostInputs: AuthoredCostInputsSchema,
  derivedCost: Type.Optional(
    Type.Integer({
      minimum: 0,
    }),
  ),
  tags: Type.Optional(Type.Array(NonEmptyStringSchema)),
  textOverrides: Type.Optional(TextOverridesSchema),
  heroProgression: Type.Optional(HeroProgressionSchema),
})

export type AuthoredCard = Static<typeof AuthoredCardSchema>
